import React, { useState, FC, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import ListingsToolbar from './ListingsToolbar';
import ProductList from './ProductList';
import { Filters } from './types';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { ActiveListingsResponse } from '../../graphql/generated';
import { GET_ACTIVE_LISTINGS } from '../../graphql/queries';
import { useLocation } from 'react-router';

const BrowseListingsPage: FC = () => {
  const listingsLimit = Number(localStorage.getItem('listingsLimit')) || 12;
  const location = useLocation();

  // Parse query params
  const queryParams = new URLSearchParams(location.search);
  const selectedCategory = queryParams.get("category");

  const [filters, setFilters] = useState<Filters>({
    brand: '',
    size: '',
    search: '',
    sortOrder: '',
    currentPage: 1,
    collectionId: selectedCategory || '',
    limit: listingsLimit,
  });

  const [allListings, setAllListings] = useState<any[]>([]);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  // 🔹 Store latest data in ref (prevents observer re-runs)
  const latestDataRef = useRef<any>(null);

  const { data, loading, error, fetchMore } = useQuery<{
    activeListings: ActiveListingsResponse;
  }>(GET_ACTIVE_LISTINGS, {
    variables: filters,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  const isInitialLoad =
    loading && filters.currentPage === 1 && allListings.length === 0;

  // 🔹 Merge new listings safely
  useEffect(() => {
    if (data?.activeListings?.listings) {
      if (filters.currentPage === 1) {
        setAllListings(data.activeListings.listings);
      } else {
        setAllListings((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newItems = data.activeListings.listings.filter(
            (p) => !existingIds.has(p.id)
          );
          return [...prev, ...newItems];
        });
      }
      setFetchingMore(false);
      setIsFilterLoading(false);
    }
  }, [data, filters.currentPage]);

  // 🔹 Reset filters when collectionId changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      collectionId: selectedCategory || '',
      currentPage: 1,
    }));
    setAllListings([]);
    setIsFilterLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedCategory]);


  // 🔹 Filter change handler (fixed: removed stale data override)
  const handleFiltersSubmit = useCallback(
    async (newFilters: Filters) => {
      setIsFilterLoading(true);

      setFilters((prev) => ({
        ...prev,
        ...newFilters,
        currentPage: 1,
      }));

      setAllListings([]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    []
  );

  // 🔹 Infinite Scroll Observer (fixed: stable observer, no re-renders)
  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        const target = entries[0];
        const latest = latestDataRef.current;

        if (!latest) return;

        const totalPages = latest?.activeListings?.pagination?.totalPages ?? 1;
        const isLastPage = (filters.currentPage ?? 1) >= totalPages;

        if (target.isIntersecting && !loading && !fetchingMore && !isLastPage) {
          setFetchingMore(true);

          setFilters((prev) => {
            const nextFilters = {
              ...prev,
              currentPage: (prev.currentPage ?? 1) + 1,
            };

            // fetchMore always receives the latest correct variables
            fetchMore({ variables: nextFilters });

            return nextFilters;
          });
        }
      },
      { threshold: 0.8, rootMargin: '200px' }
    );

    const node = loaderRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [loading, fetchingMore, fetchMore, filters.currentPage, selectedCategory]);

  const renderSkeletonGrid = (count: number) => (
    <Grid container spacing={2} sx={{ width: '100%', px: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container
      maxWidth="lg"
      id="BrowseListingContainer"
      sx={{ backgroundColor: '#f5f5f5', padding: 0, borderRadius: 2 }}
    >
      <Box mt={4} p={2}>
        <Paper elevation={3} sx={{ borderRadius: 2, p: 2 }}>
          <ListingsToolbar
            data-testid="listings-toolbar"
            onFiltersSubmit={handleFiltersSubmit}
            listingsSummary={
              data?.activeListings.listingsSummary || { brands: [], sizes: [] }
            }
          />

          {/* Per Page Dropdown */}
          <Grid item xs={12} display="flex" justifyContent="flex-end" pr={2} pb={1}>
            <FormControl
              size="small"
              variant="outlined"
              sx={{
                zIndex: 1100,
                position: 'relative',
              }}
            >
              <InputLabel id="limit-label">Per Page</InputLabel>
              <Select
                labelId="limit-label"
                label="Per Page"
                value={filters.limit || 12}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  localStorage.setItem('listingsLimit', newLimit.toString());
                  setIsFilterLoading(true);
                  setFilters((prev) => ({
                    ...prev,
                    limit: newLimit,
                    currentPage: 1,
                  }));
                  setAllListings([]);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                sx={{ minWidth: 120 }}
              >
                {[8, 12, 16, 24, 32, 40, 48].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Error */}
          {error && (
            <Typography color="error" sx={{ mt: 3 }}>
              Error: {error.message}
            </Typography>
          )}

          {/* Product List or Skeleton */}
          {(isInitialLoad || isFilterLoading) && !error?.message
            ? renderSkeletonGrid(filters.limit || 12)
            : <ProductList products={allListings} />}

          {/* Infinite Scroll Loader */}
          <Box
            ref={loaderRef}
            sx={{
              height: fetchingMore ? 'auto' : 80,
              minHeight: 80,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {fetchingMore && renderSkeletonGrid(4)}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default BrowseListingsPage;
